import { useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { format } from 'date-fns'
import { Mail } from 'lucide-react'

import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { SelectInput } from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Sales } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { SALES } from '@/constants/links'
import { useSendEmailMutation } from '@/react-query/mutations/sales'

const red_green_rules = {
  'text-red-600 font-medium': 'x<0',
  'text-amber-300 font-medium': 'x==0',
  'text-green-600 font-medium': 'x>0',
}

const monthlyPerfColDefs = createColDefs<Sales.GetMonthlyPerformanceData[number]>([
  { field: 'date', width: 100, cellStyle: { textAlign: 'center' }, hide: true },
  { field: 'year', width: 90, cellStyle: { textAlign: 'center' } },
  {
    field: 'month',
    width: 100,
    cellStyle: { textAlign: 'center' },
    valueFormatter: (params) => {
      // 2000 just placeholder, only returning the name of the month
      const date = new Date(2000, params.value - 1, 1)
      return format(date, 'MMM')
    },
  },
  {
    field: 'net_perf_pct',
    width: 120,
    headerName: 'Net Perf %',
    cellStyle: { textAlign: 'center' },
    type: 'rightAligned',
    cellClassRules: red_green_rules,
    valueFormatter: numberFormatter({ suffix: ' %' }),
  },
  {
    field: 'gross_perf_pct',
    width: 120,
    headerName: 'Gross Perf %',
    cellStyle: { textAlign: 'center' },
    type: 'rightAligned',
    cellClassRules: red_green_rules,
    valueFormatter: numberFormatter({ suffix: ' %' }),
  },
])

const yearlyPerfColDefs = createColDefs([
  { field: 'date', width: 100, cellStyle: { textAlign: 'center' }, hide: true },
  { field: 'year', width: 190, cellStyle: { textAlign: 'center' } },
  {
    field: 'net_perf_pct',
    width: 120,
    headerName: 'Net Perf %',
    cellStyle: { textAlign: 'center' },
    type: 'rightAligned',
    cellClassRules: red_green_rules,
    valueFormatter: numberFormatter({ suffix: ' %' }),
  },
  {
    field: 'gross_perf_pct',
    width: 120,
    headerName: 'Gross Perf %',
    cellStyle: { textAlign: 'center' },
    type: 'rightAligned',
    cellClassRules: red_green_rules,
    valueFormatter: numberFormatter({ suffix: ' %' }),
  },
])

const offsetColDefs = createColDefs([
  { field: 'date', width: 100, cellStyle: { textAlign: 'center' }, hide: true },
  {
    field: 'period',
    width: 120,
    cellStyle: { textAlign: 'center' },
    headerClass: 'center-aligned-group-header',
  },

  {
    headerName: 'Net',
    groupId: 'Net',
    children: [
      {
        field: 'net_perf_annualized_pct',
        type: 'numericColumn',
        headerName: 'Annualized %',
        width: 112,
        cellStyle: { textAlign: 'center' },
        cellClassRules: red_green_rules,
        valueFormatter: numberFormatter({ suffix: ' %' }),
      },
      {
        field: 'net_perf_cumulative_pct',
        type: 'numericColumn',
        headerName: 'Cumulative %',
        width: 112,
        cellStyle: { textAlign: 'center' },
        cellClassRules: red_green_rules,
        valueFormatter: numberFormatter({ suffix: ' %' }),
      },
    ],
  },

  {
    headerName: 'Gross',
    groupId: 'Gross',
    children: [
      {
        field: 'gross_perf_annualized_pct',
        type: 'numericColumn',
        headerName: 'Annualized %',
        width: 112,
        cellStyle: { textAlign: 'center' },
        cellClassRules: red_green_rules,
        valueFormatter: numberFormatter({ suffix: ' %' }),
      },
      {
        field: 'gross_perf_cumulative_pct',
        type: 'numericColumn',
        headerName: 'Cumulative %',
        width: 112,
        cellStyle: { textAlign: 'center' },
        cellClassRules: red_green_rules,
        valueFormatter: numberFormatter({ suffix: ' %' }),
      },
    ],
  },
])

const ocfScheduleColDefs = createColDefs([
  { field: 'date', width: 115, cellStyle: { textAlign: 'center' }, sort: 'desc' },
  {
    field: 'addl_bps',
    width: 115,
    cellStyle: { textAlign: 'center' },
    valueFormatter: numberFormatter(),
  },
  {
    field: 'mgnt_bps',
    width: 115,
    cellStyle: { textAlign: 'center' },
    valueFormatter: numberFormatter(),
  },
  {
    field: 'totl_bps',
    width: 115,
    cellStyle: { textAlign: 'center' },
    valueFormatter: numberFormatter(),
  },
])

const detailsColDefs = createColDefs([
  { field: 'field', width: 150 },
  { field: 'value', width: 150 },
])

export interface PerfReportPageProps {}

export const PerfReportPage: React.FC<PerfReportPageProps> = ({}) => {
  const { user } = useUserContext()
  const userEmail = user.user?.email

  const api = useApi()

  const [st_dt, setStartDate] = useState<Date | undefined>()
  const [ed_dt, setEndDate] = useState<Date | undefined>()
  const [cut_off_rule, setCutOffRule] = useState<'-1' | '1'>('-1')
  const [instrument_id, setInstrumentId] = useState<string | undefined>()

  const sendEmailMutation = useSendEmailMutation()

  const params: Sales.GetMonthlyPerformanceParams = {
    st_dt: !!st_dt && !!ed_dt ? toISO8601(st_dt) : '',
    ed_dt: ed_dt ? toISO8601(ed_dt) : undefined,
    cut_off_rule,
    instrument_id: instrument_id!,
  }

  const queries = useQueries({
    queries: [
      {
        queryKey: ['sales', 'perf-report', 'instruments'],
        queryFn: () => api.sales.getInstruments(),
      },
      {
        queryKey: ['sales', 'perf-report', 'monthly-performance', params],
        queryFn: () => api.sales.getMonthlyPerformance(params),
        enabled: !!instrument_id && !!cut_off_rule,
      },
      {
        queryKey: ['sales', 'perf-report', 'yearly-performance', params],
        queryFn: () => api.sales.getYearlyPerformance(params),
        enabled: !!instrument_id && !!cut_off_rule,
      },
      {
        queryKey: ['sales', 'perf-report', 'offset', params],
        queryFn: () => api.sales.getPerformanceOffset(params),
        enabled: !!instrument_id && !!cut_off_rule,
      },
      {
        queryKey: ['sales', 'perf-report', 'ocf', { instrument_id }],
        queryFn: () => api.sales.getOCFSchedule({ instrument_id: instrument_id! }),
        enabled: !!instrument_id,
      },
      {
        queryKey: ['sales', 'perf-report', 'details', { instrument_id }],
        queryFn: () => api.sales.getShareclassDetails({ instrument_id: instrument_id! }),
        enabled: !!instrument_id,
      },
    ],
  })

  const [instruments, monthlyPerf, yearlyPerf, offset, ocfSchedule, details] = queries

  return (
    <Page metadata={{ title: 'Perf Report' }} permissions={SALES.PERF_REPORT.permissions}>
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <FormItem>
            <Label className={'text-xs text-gray-500'}>Start Date</Label>
            <DatePicker
              size="sm"
              selected={st_dt}
              onSelect={(date) => date && setStartDate(date)}
              startMonth={new Date(2017, 0)}
              captionLayout="dropdown"
            />
          </FormItem>
          <FormItem>
            <Label className={'text-xs text-gray-500'}>End Date</Label>
            <DatePicker
              size="sm"
              selected={ed_dt}
              onSelect={(date) => date && setEndDate(date)}
              startMonth={new Date(2017, 0)}
              captionLayout="dropdown"
            />
          </FormItem>
          <FormItem>
            <Label className={'text-xs text-gray-500'}>Cut Off</Label>
            <SelectInput
              size="sm"
              value={cut_off_rule}
              onValueChange={(val: '-1' | '-1') => setCutOffRule(val)}
              options={[
                { label: 'Last Month End', value: '-1' },
                { label: 'Real time', value: '1' },
              ]}
            />
          </FormItem>
          <FormItem>
            <Label className={'text-xs text-gray-500'}>Instrument</Label>
            <SearchSelect
              size="sm"
              value={instrument_id}
              setValue={setInstrumentId}
              options={(instruments.data ?? []).map((item) => ({
                label: item.label,
                value: item.value?.toString(),
              }))}
              optionDisabled={(val) => val === null}
            />
          </FormItem>
          <div className="flex h-full">
            <div className="mt-auto">
              <LoadingButton
                icon={<Mail />}
                size="sm"
                variant="outline"
                disabled={!!!params.cut_off_rule || !!!userEmail || !!!params.instrument_id}
                onClick={() =>
                  // @ts-ignore
                  sendEmailMutation.mutateAsync({
                    ...params,
                    recipient: userEmail!,
                  })
                }
              >
                Send Email
              </LoadingButton>
            </div>
          </div>
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'horizontal'}>
        <ResizablePanel>
          <ResizablePanelGroup direction={'vertical'}>
            <ResizablePanel>
              <Widget className={'h-full'} exposed>
                <WidgetHeaderWithQuery title={'Yearly Performance'} query={yearlyPerf} />
                <WidgetContent loading={yearlyPerf.isLoading}>
                  <CustomAgGrid
                    query={yearlyPerf}
                    domLayout={'normal'}
                    rowData={yearlyPerf.data ?? []}
                    columnDefs={yearlyPerfColDefs}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel>
              <Widget className={'h-full'} exposed>
                <WidgetHeaderWithQuery title={'Monthly Performance'} query={monthlyPerf} />
                <WidgetContent loading={monthlyPerf.isLoading}>
                  <CustomAgGrid
                    query={monthlyPerf}
                    domLayout={'normal'}
                    rowData={monthlyPerf.data ?? []}
                    columnDefs={monthlyPerfColDefs}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel>
          <ResizablePanelGroup direction={'vertical'}>
            <ResizablePanel>
              <ResizablePanelGroup direction={'vertical'}>
                <ResizablePanel>
                  <Widget className={'h-full'} exposed>
                    <WidgetHeaderWithQuery
                      title="Shareclass Rolling Annualized & Cumulative Performance"
                      query={offset}
                    />
                    <WidgetContent loading={offset.isLoading}>
                      <CustomAgGrid
                        query={offset}
                        domLayout={'normal'}
                        rowData={offset.data ?? []}
                        columnDefs={offsetColDefs}
                      />
                    </WidgetContent>
                  </Widget>
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel>
                  <ResizablePanelGroup direction={'horizontal'}>
                    <ResizablePanel>
                      <Widget className={'h-full'} exposed>
                        <WidgetHeaderWithQuery title="Shareclass Details" query={details} />
                        <WidgetContent loading={details.isLoading}>
                          <CustomAgGrid
                            query={details}
                            domLayout={'normal'}
                            rowData={details.data ?? []}
                            columnDefs={detailsColDefs}
                          />
                        </WidgetContent>
                      </Widget>
                    </ResizablePanel>
                    <ResizableHandle />
                    <ResizablePanel>
                      <Widget className={'h-full'} exposed>
                        <WidgetHeaderWithQuery title="OCF Schedule" query={ocfSchedule} />
                        <WidgetContent loading={ocfSchedule.isLoading}>
                          <CustomAgGrid
                            query={ocfSchedule}
                            domLayout={'normal'}
                            rowData={ocfSchedule.data ?? []}
                            columnDefs={ocfScheduleColDefs}
                          />
                        </WidgetContent>
                      </Widget>
                    </ResizablePanel>
                  </ResizablePanelGroup>
                </ResizablePanel>
              </ResizablePanelGroup>
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
      </ResizablePanelGroup>
    </Page>
  )
}
